import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

export const ServiceM365 = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-40 -left-20 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[700px] -right-20 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-1/3 w-96 h-96 bg-gradient-to-br from-purple-200/15 to-blue-200/15 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-8">
          <Link to="/services" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center mb-4">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Retour aux services
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Cloud & Microsoft 365
          </h1>
          <p className="text-xl text-gray-600">
            Je vous aide à migrer ou à structurer vos usages autour de Microsoft 365 : messagerie, partage de fichiers, collaboration, télétravail.
          </p>
        </div>

        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <p className="text-lg text-gray-800">
              <strong>L'enjeu :</strong> que vos outils cloud soient vraiment au service de votre organisation, sécurisés, correctement configurés et simples à utiliser pour vos équipes.
            </p>
          </CardContent>
        </Card>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Mes domaines d'intervention</h2>
          <div className="space-y-3">
            {[
              'Migration de messagerie vers Exchange Online',
              'Configuration et sécurisation des boîtes mail et calendriers partagés',
              'Mise en place de SharePoint et OneDrive pour le partage de fichiers',
              'Déploiement de Teams pour la collaboration et la visioconférence',
              'Configuration des politiques de sécurité (MFA, accès conditionnel)',
              'Formation et accompagnement des utilisateurs',
              'Optimisation des licences et gestion des coûts',
              'Intégration avec vos outils métiers existants'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Les avantages</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Accessibilité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Vos données et outils accessibles partout, sur tous les appareils</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Collaboration facilitée</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Travail en équipe simplifié, partage fluide, télétravail efficace</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sécurité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Infrastructure Microsoft sécurisée, sauvegardes automatiques</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Maîtrise des coûts</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Licences adaptées à vos besoins réels, sans sur-dimensionnement</p>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="mb-8 border-gray-300">
          <CardContent className="pt-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Accompagnement personnalisé</h3>
            <p className="text-gray-600 mb-4">
              Que vous partiez de zéro ou que vous ayez déjà Microsoft 365, je vous accompagne pour structurer vos usages, 
              former vos équipes et optimiser votre configuration.
            </p>
            <p className="text-gray-600">
              L'objectif : que vos collaborateurs adoptent vraiment les outils et que vous en tiriez tous les bénéfices.
            </p>
          </CardContent>
        </Card>

        <div className="bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Envie de passer au cloud ?</h3>
          <p className="text-gray-600 mb-6">
            Parlons de votre situation actuelle et de vos objectifs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              <a href="https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled" target="_blank" rel="noopener noreferrer">
                <Phone className="w-5 h-5 mr-2" />
                Prendre RDV
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
              <Link to="/contact">
                <Mail className="w-5 h-5 mr-2" />
                Me contacter
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};